import isPropValid from '@emotion/is-prop-valid'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Amplify } from 'aws-amplify'
import { AppProps } from 'next/app'
import router, { useRouter } from 'next/router'
import { ComponentsProvider, SiteLoader } from '@/client/components'
import { CartProvider } from '@/client/context/cart'
import { ConfigProvider } from '@/client/context/Config/ConfigProvider'
import { DsProvider } from '@/client/context/ds'
import { LabelLibraryProvider } from '@/client/context/labelLibrary'
import { ELoaderProvider } from '@/client/context/Loader'
import { QueryProvider } from '@/client/provider/QueryProvider'
import { jakarta, poppins } from '@/infra/configs/fonts'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import '@/infra/styles/icomoon/index.css'
import { I18nProvider, locales } from '@/locales'
import '@/infra/styles/react-datepicker.css'
import { HydrationBoundary } from '@tanstack/react-query'
import { StyleSheetManager } from 'styled-components'
import { useEffect } from 'react'
import { htmlLang, trackNavigationHistory } from '@/client/utils'

Amplify.configure({
  aws_cognito_region: process.env.NEXT_PUBLIC_AWS_COGNITO_REGION,
  aws_user_pools_id: process.env.NEXT_PUBLIC_AWS_USER_POOLS_ID,
  aws_user_pools_web_client_id: process.env.NEXT_PUBLIC_AWS_USER_POOLS_WEB_CLIENT_ID,
  authenticationFlowType: 'USER_PASSWORD_AUTH',
  ssr: true
})

export function MyApp({ Component, pageProps }: AppProps) {
  const reactQueryDevtools = process.env.NEXT_PUBLIC_REACTQUERYDEVTOOLS === '1'
  const currentLocale = locales[(process.env.NEXT_PUBLIC_DEFAULT_LOCALE as keyof typeof locales) || 'en']
  // next/router's `locale` reflects the locale actually being served for this request/page.
  // Falling back to the build-wide NEXT_PUBLIC_DEFAULT_LOCALE here (instead of the router locale)
  // caused every page to briefly render in that default locale on the first mount, regardless of which
  // locale the visitor was actually on (e.g. order templates showing Spanish labels on an /en page).
  const { locale: routerLocale } = useRouter()

  const router = useRouter()

  // Remember the previous page, e.g. to return there after login
  useEffect(() => trackNavigationHistory(), [])

  useEffect(() => {
    const applyHtmlLang = () => {
      const lang = htmlLang[router.locale ?? '']

      if (lang) {
        document.documentElement.lang = lang
      }
    }

    applyHtmlLang()

    router.events.on('routeChangeComplete', applyHtmlLang)

    return () => {
      router.events.off('routeChangeComplete', applyHtmlLang)
    }
  }, [router])

  return (
    <StyleSheetManager
      shouldForwardProp={(propName, target) => (typeof target === 'string' ? isPropValid(propName) : true)}
    >
      <div>
        <style jsx global>
          {`
            :root {
              --poppins: ${poppins.style.fontFamily};
              --jakarta: ${jakarta.style.fontFamily};
            }
          `}
        </style>
        <I18nProvider
          locale={locales[pageProps.locale ?? routerLocale ?? process.env.NEXT_PUBLIC_DEFAULT_LOCALE ?? 'en']}
        >
          <QueryProvider pageProps={currentLocale}>
            <ConfigProvider>
              <CartProvider>
                <DsProvider>
                  <LabelLibraryProvider>
                    <ELoaderProvider>
                      <ComponentsProvider>
                        <HydrationBoundary state={pageProps.dehydratedState}>
                          <SiteLoader></SiteLoader>
                          <Component {...pageProps} />
                          {reactQueryDevtools && <ReactQueryDevtools initialIsOpen />}
                        </HydrationBoundary>
                      </ComponentsProvider>
                    </ELoaderProvider>
                  </LabelLibraryProvider>
                </DsProvider>
              </CartProvider>
            </ConfigProvider>
          </QueryProvider>
        </I18nProvider>
      </div>
    </StyleSheetManager>
  )
}

export default MyApp
